import { useEffect, useRef, useState } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import axios from "axios";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import { Country, VisaType, VisaPackFormData, VisaDocument, EMPTY_VISA_PACK_FORM } from "../types/visa";
import { API_URL } from "../config";

// ── Single-select dropdown (destination country / visa type) ───────────────
interface OptionItem {
  id: string | number;
  name: string;
}

interface CustomSelectProps {
  icon: string;
  value: string | number;
  onChange: (value: string | number) => void;
  options: OptionItem[];
  placeholder: string;
}

function CustomSelect({ icon, value, onChange, options, placeholder }: CustomSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedOption = options.find((opt) => String(opt.id) === String(value));

  return (
    <div className="avp-input-wrap" ref={dropdownRef}>
      <i className={`fa-solid ${icon} avp-input-icon`}></i>
      <div className={`avp-select-custom ${isOpen ? "is-open" : ""}`} onClick={() => setIsOpen(!isOpen)}>
        <span className={!selectedOption ? "avp-placeholder" : ""}>
          {selectedOption ? selectedOption.name : placeholder}
        </span>
        <i className="fa-solid fa-chevron-down avp-select-chevron"></i>
      </div>

      {isOpen && (
        <div className="avp-dropdown-menu">
          <div className="avp-dropdown-item" onClick={() => { onChange(""); setIsOpen(false); }}>
            {placeholder}
          </div>
          {options.map((opt) => (
            <div
              key={opt.id}
              className={`avp-dropdown-item ${String(value) === String(opt.id) ? "selected" : ""}`}
              onClick={() => { onChange(opt.id); setIsOpen(false); }}
            >
              {opt.name}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── Multi-select dropdown (eligible nationalities) ──────────────────────────
interface MultiSelectProps {
  icon: string;
  values: (string | number)[];
  onChange: (values: (string | number)[]) => void;
  options: OptionItem[];
  placeholder: string;
}

function MultiSelectCountries({ icon, values, onChange, options, placeholder }: MultiSelectProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [query, setQuery] = useState("");
  const dropdownRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const selectedIds = values.map(String);

  const toggle = (id: string) => {
    if (selectedIds.includes(id)) {
      onChange(values.filter((v) => String(v) !== id));
    } else {
      onChange([...values, id]);
    }
  };

  const filtered = query
    ? options.filter((o) => o.name.toLowerCase().includes(query.toLowerCase()))
    : options;

  const selectedNames = options.filter((o) => selectedIds.includes(String(o.id))).map((o) => o.name);

  return (
    <div className="avp-input-wrap" ref={dropdownRef}>
      <i className={`fa-solid ${icon} avp-input-icon`}></i>
      <div className={`avp-select-custom ${isOpen ? "is-open" : ""}`} onClick={() => setIsOpen(!isOpen)} style={{ minHeight: 42, height: "auto", paddingTop: 8, paddingBottom: 8 }}>
        {selectedNames.length === 0 ? (
          <span className="avp-placeholder">{placeholder}</span>
        ) : (
          <div style={{ display: "flex", flexWrap: "wrap", gap: 6 }}>
            {selectedNames.slice(0, 4).map((n) => (
              <span key={n} style={{ background: "rgba(41,75,90,.08)", color: "#294b5a", fontSize: 11.5, fontWeight: 600, padding: "3px 8px", borderRadius: 20 }}>{n}</span>
            ))}
            {selectedNames.length > 4 && (
              <span style={{ fontSize: 11.5, color: "#64748b", fontWeight: 600 }}>+{selectedNames.length - 4} more</span>
            )}
          </div>
        )}
        <i className="fa-solid fa-chevron-down avp-select-chevron"></i>
      </div>

      {isOpen && (
        <div className="avp-dropdown-menu" style={{ maxHeight: 280 }}>
          <input
            type="text"
            placeholder="Search countries…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onClick={(e) => e.stopPropagation()}
            style={{ width: "100%", boxSizing: "border-box", border: "1px solid #e2e8f0", borderRadius: 6, padding: "7px 10px", fontSize: 12.5, marginBottom: 6, outline: "none" }}
          />
          {values.length > 0 && (
            <div
              className="avp-dropdown-item"
              style={{ color: "#dc2626", fontWeight: 600 }}
              onClick={() => onChange([])}
            >
              Clear all ({values.length} selected)
            </div>
          )}
          {filtered.length === 0 && (
            <div style={{ padding: "10px 12px", fontSize: 12.5, color: "#94a3b8" }}>No countries found</div>
          )}
          
          {filtered.map((opt) => {
  const checked = selectedIds.includes(String(opt.id));
  return (
    <div
      key={opt.id}
      className={`avp-dropdown-item ${checked ? "selected" : ""}`}
      onClick={() => toggle(String(opt.id))}
      style={{
        display: "flex",
        alignItems: "center",
        justifyContent: "flex-start",
        flexWrap: "nowrap",
        gap: 10,
        width: "100%",
        boxSizing: "border-box",
      }}
    >
      <input
        type="checkbox"
        checked={checked}
        readOnly
        style={{
          width: 16,
          height: 16,
          flex: "0 0 16px",
          margin: 0,
          accentColor: "#294b5a",
          cursor: "pointer",
        }}
      />
      <span
        style={{
          flex: "1 1 auto",
          minWidth: 0,
          textAlign: "left",
          lineHeight: 1.35,
        }}
      >
        {opt.name}
      </span>
    </div>
  );
})}
        </div>
      )}
    </div>
  );
}

const EditVisaPack = () => {
  const navigate = useNavigate();
  const { id } = useParams<{ id: string }>();

  const [formData, setFormData] = useState<VisaPackFormData>({ ...EMPTY_VISA_PACK_FORM });
  const [countries, setCountries] = useState<Country[]>([]);
  const [visaTypes, setVisaTypes] = useState<VisaType[]>([]);
  const [isFetching, setIsFetching] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [docNameEn, setDocNameEn] = useState("");
  const [docNameAr, setDocNameAr] = useState("");

  // ── Skeleton Loader Component ──────────────────────────────────────────────
// ── Skeleton Loader Component ──────────────────────────────────────────────
function SkeletonLoader() {
  return (
    <div style={{ width: "100%", minHeight: "100vh", backgroundColor: "#f4f6f8", padding: "24px 20px 40px", boxSizing: "border-box" }}>
      <div className="avp-container">
        <div className="d-flex justify-content-between align-items-end mb-4">
          <div>
            <div style={{ 
              width: 200, 
              height: 14,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
              backgroundSize: "200% 100%",
              borderRadius: 4,
              marginBottom: 8,
              animation: "shimmer 1.5s infinite"
            }}></div>
            <div style={{ 
              width: 280, 
              height: 32, 
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
              backgroundSize: "200% 100%",
              borderRadius: 6,
              animation: "shimmer 1.5s infinite"
            }}></div>
            <div style={{ 
              width: 120, 
              height: 18, 
              marginTop: 6,
              background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
              backgroundSize: "200% 100%",
              borderRadius: 4,
              animation: "shimmer 1.5s infinite"
            }}></div>
          </div>
          <div style={{ 
            width: 140, 
            height: 42, 
            background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
            backgroundSize: "200% 100%",
            borderRadius: 8,
            animation: "shimmer 1.5s infinite"
          }}></div>
        </div>

        <div className="row g-4">
          <div className="col-12 col-lg-8">
            {/* Basic Route Information Skeleton */}
            <div className="avp-card">
              <div className="avp-card-head">
                <div className="avp-card-icon" style={{ background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.5s infinite" }}>
                  <i className="fa-solid fa-passport" style={{ opacity: 0.3 }}></i>
                </div>
                <div>
                  <div style={{ 
                    width: 160, 
                    height: 18,
                    background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                    backgroundSize: "200% 100%",
                    borderRadius: 4,
                    marginBottom: 4,
                    animation: "shimmer 1.5s infinite"
                  }}></div>
                  <div style={{ 
                    width: 250, 
                    height: 14,
                    background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                    backgroundSize: "200% 100%",
                    borderRadius: 4,
                    animation: "shimmer 1.5s infinite"
                  }}></div>
                </div>
              </div>
              <div className="avp-card-body">
                <div className="row g-3">
                  {[...Array(8)].map((_, i) => (
                    <div key={i} className={i < 2 ? "col-12 col-md-6" : i < 6 ? "col-12" : "col-12"}>
                      <div style={{ 
                        height: 14, 
                        width: i === 0 ? 160 : i === 1 ? 160 : i === 2 ? 180 : i === 3 ? 180 : 140,
                        background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                        backgroundSize: "200% 100%",
                        borderRadius: 4,
                        marginBottom: 6,
                        animation: "shimmer 1.5s infinite"
                      }}></div>
                      <div style={{ 
                        height: 42, 
                        width: "100%",
                        background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                        backgroundSize: "200% 100%",
                        borderRadius: 8,
                        animation: "shimmer 1.5s infinite"
                      }}></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Visa Details Skeleton */}
            <div className="avp-card">
              <div className="avp-card-head">
                <div className="avp-card-icon" style={{ background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.5s infinite" }}>
                  <i className="fa-solid fa-sliders" style={{ opacity: 0.3 }}></i>
                </div>
                <div>
                  <div style={{ 
                    width: 180, 
                    height: 18,
                    background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                    backgroundSize: "200% 100%",
                    borderRadius: 4,
                    marginBottom: 4,
                    animation: "shimmer 1.5s infinite"
                  }}></div>
                  <div style={{ 
                    width: 260, 
                    height: 14,
                    background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                    backgroundSize: "200% 100%",
                    borderRadius: 4,
                    animation: "shimmer 1.5s infinite"
                  }}></div>
                </div>
              </div>
              <div className="avp-card-body">
                <div className="row g-3">
                  {[...Array(12)].map((_, i) => (
                    <div key={i} className={i < 3 ? "col-12 col-md-4" : i < 7 ? "col-12 col-md-6" : "col-12"}>
                      <div style={{ 
                        height: 14, 
                        width: i === 0 ? 100 : i === 1 ? 100 : i === 2 ? 100 : i < 7 ? 140 : 160,
                        background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                        backgroundSize: "200% 100%",
                        borderRadius: 4,
                        marginBottom: 6,
                        animation: "shimmer 1.5s infinite"
                      }}></div>
                      <div style={{ 
                        height: i < 3 ? 42 : i < 7 ? 42 : 90,
                        width: "100%",
                        background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                        backgroundSize: "200% 100%",
                        borderRadius: i < 3 ? 8 : 8,
                        animation: "shimmer 1.5s infinite"
                      }}></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>

            {/* Documents Skeleton */}
            <div className="avp-card">
              <div className="avp-card-head">
                <div className="avp-card-icon" style={{ background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.5s infinite" }}>
                  <i className="fa-solid fa-folder-open" style={{ opacity: 0.3 }}></i>
                </div>
                <div>
                  <div style={{ 
                    width: 170, 
                    height: 18,
                    background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                    backgroundSize: "200% 100%",
                    borderRadius: 4,
                    marginBottom: 4,
                    animation: "shimmer 1.5s infinite"
                  }}></div>
                  <div style={{ 
                    width: 290, 
                    height: 14,
                    background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                    backgroundSize: "200% 100%",
                    borderRadius: 4,
                    animation: "shimmer 1.5s infinite"
                  }}></div>
                </div>
              </div>
              <div className="avp-card-body">
                <div className="avp-doc-add">
                  <div className="avp-input-wrap" style={{ flex: 1, minWidth: 220 }}>
                    <i className="fa-solid fa-file-medical avp-input-icon" style={{ opacity: 0.3 }}></i>
                    <div style={{ 
                      height: 42, 
                      width: "100%",
                      background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                      backgroundSize: "200% 100%",
                      borderRadius: 8,
                      animation: "shimmer 1.5s infinite"
                    }}></div>
                  </div>
                  <div className="avp-input-wrap" style={{ flex: 1, minWidth: 220 }}>
                    <i className="fa-solid fa-file-medical avp-input-icon" style={{ opacity: 0.3 }}></i>
                    <div style={{ 
                      height: 42, 
                      width: "100%",
                      background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                      backgroundSize: "200% 100%",
                      borderRadius: 8,
                      animation: "shimmer 1.5s infinite"
                    }}></div>
                  </div>
                  <div style={{ 
                    height: 42, 
                    width: 150,
                    background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                    backgroundSize: "200% 100%",
                    borderRadius: 8,
                    animation: "shimmer 1.5s infinite"
                  }}></div>
                </div>
                <div className="avp-doc-list">
                  {[...Array(3)].map((_, i) => (
                    <div key={i} className="avp-doc-row">
                      <div className="d-flex align-items-center" style={{ flex: 1 }}>
                        <span className="avp-doc-num" style={{ background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", backgroundSize: "200% 100%", animation: "shimmer 1.5s infinite", color: "transparent" }}>{i + 1}</span>
                        <div style={{ 
                          height: 14, 
                          width: "60%",
                          background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                          backgroundSize: "200% 100%",
                          borderRadius: 4,
                          animation: "shimmer 1.5s infinite"
                        }}></div>
                      </div>
                      <div style={{ 
                        width: 30, 
                        height: 30,
                        background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                        backgroundSize: "200% 100%",
                        borderRadius: 6,
                        animation: "shimmer 1.5s infinite"
                      }}></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>

          {/* Sidebar Skeleton */}
          <div className="col-12 col-lg-4">
            <div className="avp-sidebar-card">
              <div style={{ 
                height: 14, 
                width: "40%",
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                backgroundSize: "200% 100%",
                borderRadius: 4,
                marginBottom: 16,
                animation: "shimmer 1.5s infinite"
              }}></div>
              
              <div className="row g-2 mb-3">
                <div className="col-8">
                  <div style={{ 
                    height: 14, 
                    width: "40%",
                    background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                    backgroundSize: "200% 100%",
                    borderRadius: 4,
                    marginBottom: 6,
                    animation: "shimmer 1.5s infinite"
                  }}></div>
                  <div style={{ 
                    height: 42, 
                    width: "100%",
                    background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                    backgroundSize: "200% 100%",
                    borderRadius: 8,
                    animation: "shimmer 1.5s infinite"
                  }}></div>
                </div>
                <div className="col-4">
                  <div style={{ 
                    height: 14, 
                    width: "50%",
                    background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                    backgroundSize: "200% 100%",
                    borderRadius: 4,
                    marginBottom: 6,
                    animation: "shimmer 1.5s infinite"
                  }}></div>
                  <div style={{ 
                    height: 42, 
                    width: "100%",
                    background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                    backgroundSize: "200% 100%",
                    borderRadius: 8,
                    animation: "shimmer 1.5s infinite"
                  }}></div>
                </div>
              </div>

              <div style={{ 
                height: 18, 
                width: "30%",
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                backgroundSize: "200% 100%",
                borderRadius: 4,
                marginBottom: 16,
                animation: "shimmer 1.5s infinite"
              }}></div>

              <div style={{ 
                backgroundColor: "#f8fafc", 
                borderRadius: "8px", 
                padding: "16px", 
                border: "1px solid #e2e8f0", 
                marginBottom: "20px" 
              }}>
                {[...Array(5)].map((_, i) => (
                  <div key={i} className="avp-summary-item">
                    <div style={{ 
                      height: 14, 
                      width: "35%",
                      background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                      backgroundSize: "200% 100%",
                      borderRadius: 4,
                      animation: "shimmer 1.5s infinite"
                    }}></div>
                    <div style={{ 
                      height: 14, 
                      width: "30%",
                      background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                      backgroundSize: "200% 100%",
                      borderRadius: 4,
                      animation: "shimmer 1.5s infinite"
                    }}></div>
                  </div>
                ))}
              </div>

              <div style={{ 
                height: 42, 
                width: "100%",
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                backgroundSize: "200% 100%",
                borderRadius: 8,
                marginBottom: 8,
                animation: "shimmer 1.5s infinite"
              }}></div>
              <div style={{ 
                height: 42, 
                width: "100%",
                background: "linear-gradient(90deg, #e2e8f0 25%, #f1f5f9 50%, #e2e8f0 75%)", 
                backgroundSize: "200% 100%",
                borderRadius: 8,
                animation: "shimmer 1.5s infinite"
              }}></div>
            </div>
          </div>
        </div>
      </div>
      
      <style>{`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}</style>
    </div>
  );
}

  useEffect(() => {
    const fetchData = async () => {
      try {
        setIsFetching(true);

        const formDataResponse = await axios.get(`${API_URL}/visa-packs/form-data`);
        setCountries(formDataResponse.data.data.countries);
        setVisaTypes(formDataResponse.data.data.visaTypes);

        if (id) {
          const visaPackResponse = await axios.get(`${API_URL}/visa-packs/${id}`);

          if (visaPackResponse.data.status) {
            const pack = visaPackResponse.data.data;

            setFormData({
              name_en: pack.name_en || "",
              name_ar: pack.name_ar || "",
              description_en: pack.description_en || "",
              description_ar: pack.description_ar || "",
              destination_country_id: pack.destination_country_id?.toString() || "",
              nationality_ids: (pack.nationalities || []).map((n: any) => String(n.id)),
              visa_type_id: pack.visa_type_id?.toString() || "",
              visa_issue_type: pack.visa_issue_type || "eVisa",
              price: pack.price != null ? String(pack.price) : "",
              currency: pack.currency || "OMR",
              processing_time_en: pack.processing_time_en || "",
              processing_time_ar: pack.processing_time_ar || "",
              visa_validity_en: pack.visa_validity_en || "",
              visa_validity_ar: pack.visa_validity_ar || "",
              stay_validity_en: pack.stay_validity_en || "",
              stay_validity_ar: pack.stay_validity_ar || "",
              entry_type: pack.entry_type || "Single Entry",
              visa_detail_en: pack.visa_detail_en || "",
              visa_detail_ar: pack.visa_detail_ar || "",
              is_active: pack.is_active ?? true,
              documents: (pack.documents || []).map((doc: any) => ({
                document_name_en: doc.document_name_en,
                document_name_ar: doc.document_name_ar || undefined,
                priority: doc.priority,
              })),
            });
          }
        }
      } catch (error: any) {
        console.error("Error fetching data:", error);
        toast.error("Failed to load visa pack data");
        if (error.response?.status === 404) {
          navigate("/admin/list-packs");
        }
      } finally {
        setIsFetching(false);
      }
    };

    fetchData();
  }, [id, navigate]);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement>) => {
    const { name, value, type } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: type === "checkbox" ? (e.target as HTMLInputElement).checked : value,
    }));
  };

  const set = (key: keyof VisaPackFormData, value: any) => setFormData((prev) => ({ ...prev, [key]: value }));

  const handleAddDocument = () => {
    if (!docNameEn.trim()) {
      toast.error("Document name (English) is required");
      return;
    }
    const newDocument: VisaDocument = {
      document_name_en: docNameEn,
      document_name_ar: docNameAr || undefined,
      priority: formData.documents.length + 1,
    };
    setFormData((prev) => ({ ...prev, documents: [...prev.documents, newDocument] }));
    setDocNameEn("");
    setDocNameAr("");
  };

  const handleRemoveDocument = (index: number) => {
    setFormData((prev) => ({ ...prev, documents: prev.documents.filter((_, i) => i !== index) }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!formData.name_en || !formData.destination_country_id || !formData.visa_type_id || !formData.price) {
      toast.error("Please fill in all required fields");
      return;
    }

    setIsSaving(true);
    try {
      const response = await axios.put(`${API_URL}/visa-packs/${id}`, formData);

      if (response.data.status) {
        toast.success("Visa pack updated successfully!");
        navigate("/admin/list-packs");
      }
    } catch (error: any) {
      console.error("Error updating visa pack:", error.response?.data || error.message);
      toast.error(error.response?.data?.message || "Error updating visa pack");
    } finally {
      setIsSaving(false);
    }
  };

  if (isFetching) {
    return <SkeletonLoader />;
  }

  return (
    <>
      <style>{`
        .avp-container { max-width: 1280px; margin: 0 auto; }
        .avp-card { background: #ffffff; border-radius: 12px; border: 1px solid #e2e8f0 !important; box-shadow: 0 4px 16px -2px rgba(0, 0, 0, 0.03); margin-bottom: 24px; transition: border-color 0.2s ease; }
        .avp-card:hover { border-color: #cbd5e1; }
        .avp-card-head { display: flex; align-items: center; gap: 14px; padding: 20px 24px; background: #ffffff; border-bottom: 1px solid #f1f5f9; }
        .avp-card-icon { width: 40px; height: 40px; border-radius: 10px; background: rgba(41, 75, 90, 0.08); color: #294b5a; display: flex; align-items: center; justify-content: center; font-size: 15px; flex-shrink: 0; }
        .avp-card-title { font-size: 15px; font-weight: 700; color: #294b5a; letter-spacing: -0.01em; }
        .avp-card-sub { font-size: 12.5px; color: #64748b; margin-top: 2px; }
        .avp-card-body { padding: 24px; }
        .avp-lang-tag { display: inline-block; font-size: 10.5px; font-weight: 700; letter-spacing: .04em; color: #94a3b8; margin-left: 6px; }
        .avp-label { display: block; font-size: 12.5px; font-weight: 600; color: #334155; margin-bottom: 7px; }
        .avp-label .req { color: #dc2626; margin-left: 2px; }
        .avp-input-wrap { position: relative; }
        .avp-input-icon { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); color: #94a3b8; font-size: 13px; pointer-events: none; transition: color 0.15s ease; }
        .avp-input, .avp-textarea, .avp-select { width: 100%; font-size: 13.5px; color: #0f172a; border: 1px solid #cbd5e1 !important; border-radius: 8px; background: #f8fafc; transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1); }
        .avp-input, .avp-select { height: 42px; padding: 0 14px 0 38px; }
        .avp-input--no-icon { padding-left: 14px; }
        .avp-textarea { padding: 12px 14px; resize: vertical; line-height: 1.5; min-height: 90px; }
        .avp-input::placeholder, .avp-textarea::placeholder { color: #94a3b8; }
        .avp-input:focus, .avp-textarea:focus, .avp-select:focus { outline: none; border-color: #294b5a; background: #ffffff; box-shadow: 0 0 0 3px rgba(41, 75, 90, 0.1); }
        .avp-select { appearance: none; -webkit-appearance: none; padding-right: 36px; cursor: pointer; }
        .avp-select-chevron { position: absolute; right: 14px; top: 50%; transform: translateY(-50%); pointer-events: none; color: #94a3b8; font-size: 11px; }
        .avp-fee-currency { position: absolute; left: 14px; top: 50%; transform: translateY(-50%); font-size: 12.5px; font-weight: 700; color: #294b5a; pointer-events: none; }
        .avp-fee-input { padding-left: 54px !important; font-weight: 600; }
        .avp-doc-add { display: flex; gap: 10px; flex-wrap: wrap; }
        .avp-doc-add-btn { height: 42px; padding: 0 20px; border-radius: 8px; border: none; background: #294b5a; color: #ffffff; font-size: 13px; font-weight: 600; cursor: pointer; display: inline-flex; align-items: center; gap: 8px; white-space: nowrap; transition: all 0.2s ease; }
        .avp-doc-add-btn:hover { background: #1e3743; }
        .avp-doc-list { margin-top: 16px; border: 1px solid #e2e8f0 !important; border-radius: 10px; overflow: hidden; }
        .avp-doc-row { display: flex; align-items: center; justify-content: space-between; padding: 12px 16px; font-size: 13.5px; color: #1e293b; background: #ffffff; transition: background 0.15s ease; }
        .avp-doc-row:hover { background: #f8fafc; }
        .avp-doc-row + .avp-doc-row { border-top: 1px solid #f1f5f9; }
        .avp-doc-num { display: inline-flex; align-items: center; justify-content: center; width: 22px; height: 22px; border-radius: 50%; background: rgba(211, 190, 118, 0.2); color: #8c7329; font-size: 11px; font-weight: 700; margin-right: 12px; flex-shrink: 0; }
        .avp-doc-remove { width: 30px; height: 30px; border-radius: 6px; border: 1px solid transparent !important; background: transparent; color: #94a3b8; display: flex; align-items: center; justify-content: center; cursor: pointer; transition: all 0.15s ease; }
        .avp-doc-remove:hover { background: #fef2f2; border-color: #fecaca; color: #dc2626; }
        .avp-sidebar-card { position: sticky; top: 20px; background: #ffffff; border-radius: 12px; border: 1px solid #e2e8f0 !important; box-shadow: 0 4px 16px -2px rgba(0, 0, 0, 0.04); padding: 24px; }
        .avp-summary-item { display: flex; justify-content: space-between; align-items: center; padding: 10px 0; border-bottom: 1px dashed #e2e8f0; font-size: 13px; }
        .avp-summary-item:last-child { border-bottom: none; }
        .avp-btn { height: 42px; padding: 0 24px; font-size: 13.5px; font-weight: 600; border-radius: 8px; cursor: pointer; border: none; display: inline-flex; align-items: center; justify-content: center; gap: 8px; transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1); text-decoration: none; }
        .avp-btn--ghost { background: #ffffff; color: #475569; border: 1px solid #cbd5e1 !important; }
        .avp-btn--ghost:hover { background: #f8fafc; color: #0f172a; border-color: #94a3b8; }
        .avp-btn--primary { background: #294b5a; color: #ffffff; box-shadow: 0 2px 8px rgba(41, 75, 90, 0.25); width: 100%; }
        .avp-btn--primary:hover:not(:disabled) { background: #1e3743; box-shadow: 0 4px 12px rgba(41, 75, 90, 0.35); }
        .avp-btn--primary:disabled { opacity: 0.6; cursor: not-allowed; }
        .avp-btn--primary i { color: #d3be76; }
        .avp-select-custom { width: 100%; min-height: 42px; padding: 0 36px 0 38px; display: flex; align-items: center; font-size: 13.5px; color: #0f172a; border: 1px solid #cbd5e1 !important; border-radius: 8px; background: #f8fafc; cursor: pointer; user-select: none; transition: all 0.2s cubic-bezier(0.4, 0, 0.2, 1); position: relative; }
        .avp-select-custom.is-open, .avp-select-custom:hover { border-color: #294b5a; background: #ffffff; }
        .avp-placeholder { color: #94a3b8; }
        .avp-dropdown-menu { position: absolute; top: calc(100% + 4px); left: 0; right: 0; max-height: 210px; overflow-y: auto; background: #ffffff; border: 1px solid #e2e8f0 !important; border-radius: 8px; box-shadow: 0 10px 25px -5px rgba(0, 0, 0, 0.1); z-index: 9999; padding: 4px; }
        .avp-dropdown-menu::-webkit-scrollbar { width: 6px; }
        .avp-dropdown-menu::-webkit-scrollbar-thumb { background: #cbd5e1; border-radius: 4px; }
       /* Remplace/complète les règles existantes .avp-dropdown-item */
.avp-dropdown-item {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 9px 12px;
  font-size: 13px;
  color: #334155;
  border-radius: 6px;
  cursor: pointer;
  transition: background 0.15s ease;
  text-align: left;
}
.avp-dropdown-item:hover { background: #f1f5f9; color: #294b5a; }
.avp-dropdown-item.selected { background: rgba(41, 75, 90, 0.08); color: #294b5a; font-weight: 600; }

.avp-dropdown-checkbox {
  width: 16px;
  height: 16px;
  flex-shrink: 0;
  margin: 0;
  accent-color: #294b5a;
  cursor: pointer;
}
.avp-dropdown-item-label {
  flex: 1;
  min-width: 0;
  text-align: left;
  line-height: 1.35;
}
        .avp-input-wrap:focus-within, .avp-input-wrap:hover { z-index: 999; }
      `}</style>

      <div style={{ width: "100%", minHeight: "100vh", backgroundColor: "#f4f6f8", padding: "24px 20px 40px", boxSizing: "border-box" }}>
        <ToastContainer position="top-right" autoClose={3000} />
        <div className="avp-container">
          <div className="d-flex justify-content-between align-items-end mb-4">
            <div>
              <div className="d-flex align-items-center gap-2 mb-1" style={{ fontSize: "13px", color: "#64748b" }}>
                <Link to="/admin/list-packs" style={{ color: "#64748b", textDecoration: "none", fontWeight: 500 }}>Visa Packs</Link>
                <i className="fa-solid fa-chevron-right" style={{ fontSize: "10px", color: "#94a3b8" }}></i>
                <span style={{ color: "#294b5a", fontWeight: 600 }}>Edit Pack</span>
              </div>
              <h1 style={{ color: "#294b5a", fontSize: "22px", fontWeight: 700, margin: 0, letterSpacing: "-0.02em" }}>Edit Visa Package</h1>
              <p style={{ color: "#64748b", fontSize: "13.5px", margin: "4px 0 0" }}>{formData.name_en || "—"}</p>
            </div>
            <Link to="/admin/list-packs" className="avp-btn avp-btn--ghost">
              <i className="fa-solid fa-arrow-left"></i>
              Back to Packs
            </Link>
          </div>

          <form onSubmit={handleSubmit}>
            <div className="row g-4">
              <div className="col-12 col-lg-8">
                {/* BASIC ROUTE INFORMATION */}
                <div className="avp-card">
                  <div className="avp-card-head">
                    <div className="avp-card-icon"><i className="fa-solid fa-passport"></i></div>
                    <div>
                      <div className="avp-card-title">Basic Route & Info</div>
                      <div className="avp-card-sub">Package name, destination and eligible nationalities</div>
                    </div>
                  </div>
                  <div className="avp-card-body">
                    <div className="row g-3">
                      <div className="col-12 col-md-6">
                        <label className="avp-label">Visa Pack Name <span className="avp-lang-tag">EN</span><span className="req">*</span></label>
                        <div className="avp-input-wrap">
                          <i className="fa-solid fa-heading avp-input-icon"></i>
                          <input type="text" name="name_en" className="avp-input" value={formData.name_en} onChange={handleInputChange} required />
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="avp-label">Visa Pack Name <span className="avp-lang-tag">AR</span></label>
                        <div className="avp-input-wrap">
                          <i className="fa-solid fa-heading avp-input-icon"></i>
                          <input type="text" name="name_ar" className="avp-input" dir="rtl" value={formData.name_ar} onChange={handleInputChange} />
                        </div>
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="avp-label">Destination Country<span className="req">*</span></label>
                        <CustomSelect
                          icon="fa-location-dot"
                          value={formData.destination_country_id}
                          onChange={(val) => set("destination_country_id", String(val))}
                          options={countries}
                          placeholder="Select country"
                        />
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="avp-label">Eligible Nationalities</label>
                        <MultiSelectCountries
                          icon="fa-flag"
                          values={formData.nationality_ids}
                          onChange={(vals) => set("nationality_ids", vals)}
                          options={countries}
                          placeholder="Leave empty if eligibility must be verified live"
                        />
                      </div>

                      <div className="col-12">
                        <label className="avp-label">Eligibility Notes <span className="avp-lang-tag">EN</span></label>
                        <textarea name="description_en" className="avp-textarea" value={formData.description_en} onChange={handleInputChange} />
                      </div>
                      <div className="col-12">
                        <label className="avp-label">Eligibility Notes <span className="avp-lang-tag">AR</span></label>
                        <textarea name="description_ar" className="avp-textarea" dir="rtl" value={formData.description_ar} onChange={handleInputChange} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* VISA DETAILS & SPECIFICATIONS */}
                <div className="avp-card">
                  <div className="avp-card-head">
                    <div className="avp-card-icon"><i className="fa-solid fa-sliders"></i></div>
                    <div>
                      <div className="avp-card-title">Visa Type & Specifications</div>
                      <div className="avp-card-sub">Issuance parameters, timelines and descriptions</div>
                    </div>
                  </div>
                  <div className="avp-card-body">
                    <div className="row g-3">
                      <div className="col-12 col-md-4">
                        <label className="avp-label">Visa Type<span className="req">*</span></label>
                        <CustomSelect
                          icon="fa-id-card"
                          value={formData.visa_type_id}
                          onChange={(val) => set("visa_type_id", String(val))}
                          options={visaTypes}
                          placeholder="Select visa type"
                        />
                      </div>

                      <div className="col-12 col-md-4">
                        <label className="avp-label">Issue Type</label>
                        <div className="avp-input-wrap">
                          <i className="fa-solid fa-stamp avp-input-icon"></i>
                          <select name="visa_issue_type" className="avp-select" value={formData.visa_issue_type} onChange={handleInputChange} required>
                            <option value="eVisa">eVisa</option>
                            <option value="Regular">Regular</option>
                            <option value="Express">Express</option>
                            <option value="Premium">Premium</option>
                          </select>
                          <i className="fa-solid fa-chevron-down avp-select-chevron"></i>
                        </div>
                      </div>

                      <div className="col-12 col-md-4">
                        <label className="avp-label">Entry Type</label>
                        <div className="avp-input-wrap">
                          <i className="fa-solid fa-door-open avp-input-icon"></i>
                          <select name="entry_type" className="avp-select" value={formData.entry_type} onChange={handleInputChange} required>
                            <option value="Single Entry">Single Entry</option>
                            <option value="Multiple Entry">Multiple Entry</option>
                            <option value="Double Entry">Double Entry</option>
                            <option value="Transit">Transit</option>
                          </select>
                          <i className="fa-solid fa-chevron-down avp-select-chevron"></i>
                        </div>
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="avp-label">Processing Time <span className="avp-lang-tag">EN</span><span className="req">*</span></label>
                        <div className="avp-input-wrap">
                          <i className="fa-solid fa-clock avp-input-icon"></i>
                          <input type="text" name="processing_time_en" className="avp-input" value={formData.processing_time_en} onChange={handleInputChange} required />
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="avp-label">Processing Time <span className="avp-lang-tag">AR</span></label>
                        <div className="avp-input-wrap">
                          <i className="fa-solid fa-clock avp-input-icon"></i>
                          <input type="text" name="processing_time_ar" className="avp-input" dir="rtl" value={formData.processing_time_ar} onChange={handleInputChange} />
                        </div>
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="avp-label">Visa Validity <span className="avp-lang-tag">EN</span><span className="req">*</span></label>
                        <div className="avp-input-wrap">
                          <i className="fa-solid fa-calendar-check avp-input-icon"></i>
                          <input type="text" name="visa_validity_en" className="avp-input" value={formData.visa_validity_en} onChange={handleInputChange} required />
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="avp-label">Visa Validity <span className="avp-lang-tag">AR</span></label>
                        <div className="avp-input-wrap">
                          <i className="fa-solid fa-calendar-check avp-input-icon"></i>
                          <input type="text" name="visa_validity_ar" className="avp-input" dir="rtl" value={formData.visa_validity_ar} onChange={handleInputChange} />
                        </div>
                      </div>

                      <div className="col-12 col-md-6">
                        <label className="avp-label">Stay Validity <span className="avp-lang-tag">EN</span><span className="req">*</span></label>
                        <div className="avp-input-wrap">
                          <i className="fa-solid fa-bed avp-input-icon"></i>
                          <input type="text" name="stay_validity_en" className="avp-input" value={formData.stay_validity_en} onChange={handleInputChange} required />
                        </div>
                      </div>
                      <div className="col-12 col-md-6">
                        <label className="avp-label">Stay Validity <span className="avp-lang-tag">AR</span></label>
                        <div className="avp-input-wrap">
                          <i className="fa-solid fa-bed avp-input-icon"></i>
                          <input type="text" name="stay_validity_ar" className="avp-input" dir="rtl" value={formData.stay_validity_ar} onChange={handleInputChange} />
                        </div>
                      </div>

                      <div className="col-12">
                        <label className="avp-label">Package Details <span className="avp-lang-tag">EN</span><span className="req">*</span></label>
                        <textarea name="visa_detail_en" className="avp-textarea" value={formData.visa_detail_en} onChange={handleInputChange} required />
                      </div>
                      <div className="col-12">
                        <label className="avp-label">Package Details <span className="avp-lang-tag">AR</span></label>
                        <textarea name="visa_detail_ar" className="avp-textarea" dir="rtl" value={formData.visa_detail_ar} onChange={handleInputChange} />
                      </div>
                    </div>
                  </div>
                </div>

                {/* REQUIRED DOCUMENTS */}
                <div className="avp-card">
                  <div className="avp-card-head">
                    <div className="avp-card-icon"><i className="fa-solid fa-folder-open"></i></div>
                    <div>
                      <div className="avp-card-title">Required Documents</div>
                      <div className="avp-card-sub">List the mandatory documents required from the applicant</div>
                    </div>
                  </div>
                  <div className="avp-card-body">
                    <div className="avp-doc-add">
                      <div className="avp-input-wrap" style={{ flex: 1, minWidth: 220 }}>
                        <i className="fa-solid fa-file-medical avp-input-icon"></i>
                        <input type="text" className="avp-input" placeholder="Document name (English)"
                          value={docNameEn} onChange={(e) => setDocNameEn(e.target.value)} />
                      </div>
                      <div className="avp-input-wrap" style={{ flex: 1, minWidth: 220 }}>
                        <i className="fa-solid fa-file-medical avp-input-icon"></i>
                        <input type="text" className="avp-input" dir="rtl" placeholder="اسم المستند (اختياري)"
                          value={docNameAr} onChange={(e) => setDocNameAr(e.target.value)}
                          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); handleAddDocument(); } }} />
                      </div>
                      <button type="button" onClick={handleAddDocument} className="avp-doc-add-btn">
                        <i className="fa-solid fa-plus"></i>
                        Add Document
                      </button>
                    </div>

                    <div className="avp-doc-list">
                      {formData.documents.length > 0 ? (
                        formData.documents.map((doc, index) => (
                          <div key={index} className="avp-doc-row">
                            <div className="d-flex align-items-center">
                              <span className="avp-doc-num">{index + 1}</span>
                              <span style={{ fontWeight: 500 }}>
                                {doc.document_name_en}
                                {doc.document_name_ar && <span style={{ color: "#94a3b8", marginLeft: 8 }}>· {doc.document_name_ar}</span>}
                              </span>
                            </div>
                            <button type="button" onClick={() => handleRemoveDocument(index)} className="avp-doc-remove" title="Remove Document">
                              <i className="fa-solid fa-trash-can"></i>
                            </button>
                          </div>
                        ))
                      ) : (
                        <div style={{ padding: "32px 16px", textAlign: "center", color: "#94a3b8", fontSize: "13.5px" }}>
                          <i className="fa-regular fa-folder-open mb-2" style={{ fontSize: "24px", display: "block", color: "#cbd5e1" }}></i>
                          No required documents added yet.
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              {/* SIDEBAR SUMMARY & ACTIONS */}
              <div className="col-12 col-lg-4">
                <div className="avp-sidebar-card">
                  <div style={{ fontSize: "12px", fontWeight: 700, textTransform: "uppercase", letterSpacing: "0.5px", color: "#94a3b8", marginBottom: "12px" }}>
                    Pricing & Summary
                  </div>

                  <div className="row g-2 mb-3">
                    <div className="col-8">
                      <label className="avp-label">Service Price<span className="req">*</span></label>
                      <div className="avp-input-wrap">
                        <span className="avp-fee-currency">{formData.currency}</span>
                        <input type="number" name="price" className="avp-input avp-fee-input" step="0.01" min="0"
                          value={formData.price} onChange={handleInputChange} required />
                      </div>
                    </div>
                    <div className="col-4">
                      <label className="avp-label">Currency</label>
                      <div className="avp-input-wrap">
                        <select name="currency" className="avp-select avp-input--no-icon" value={formData.currency} onChange={handleInputChange} required>
                          <option value="OMR">OMR</option>
                          <option value="USD">USD</option>
                          <option value="AED">AED</option>
                          <option value="SAR">SAR</option>
                          <option value="QAR">QAR</option>
                          <option value="BHD">BHD</option>
                          <option value="KWD">KWD</option>
                          <option value="EUR">EUR</option>
                          <option value="GBP">GBP</option>
                        </select>
                        <i className="fa-solid fa-chevron-down avp-select-chevron"></i>
                      </div>
                    </div>
                  </div>

                  <label style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, color: "#334155", cursor: "pointer", marginBottom: 20 }}>
                    <input type="checkbox" name="is_active" checked={formData.is_active} onChange={handleInputChange} />
                    Active (visible to customers)
                  </label>

                  <div style={{ backgroundColor: "#f8fafc", borderRadius: "8px", padding: "16px", border: "1px solid #e2e8f0", marginBottom: "20px" }}>
                    <div className="avp-summary-item">
                      <span style={{ color: "#64748b" }}>Package Name</span>
                      <span style={{ fontWeight: 600, color: "#0f172a", maxWidth: "150px", textAlign: "right" }} className="text-truncate">
                        {formData.name_en || "—"}
                      </span>
                    </div>
                    <div className="avp-summary-item">
                      <span style={{ color: "#64748b" }}>Issue Type</span>
                      <span style={{ fontWeight: 600, color: "#294b5a" }}>{formData.visa_issue_type}</span>
                    </div>
                    <div className="avp-summary-item">
                      <span style={{ color: "#64748b" }}>Entry</span>
                      <span style={{ fontWeight: 600, color: "#0f172a" }}>{formData.entry_type}</span>
                    </div>
                    <div className="avp-summary-item">
                      <span style={{ color: "#64748b" }}>Nationalities</span>
                      <span style={{ fontWeight: 600, color: "#0f172a" }}>
                        {formData.nationality_ids.length > 0 ? formData.nationality_ids.length : "Verify live"}
                      </span>
                    </div>
                    <div className="avp-summary-item">
                      <span style={{ color: "#64748b" }}>Documents</span>
                      <span style={{ fontWeight: 700, backgroundColor: "rgba(211,190,118,0.2)", color: "#8c7329", padding: "2px 8px", borderRadius: "10px", fontSize: "11px" }}>
                        {formData.documents.length} Required
                      </span>
                    </div>
                  </div>

                  <button type="submit" disabled={isSaving} className="avp-btn avp-btn--primary mb-2">
                    <i className="fa-solid fa-check"></i>
                    {isSaving ? "Saving Changes..." : "Save Changes"}
                  </button>

                  <Link to="/admin/list-packs" className="avp-btn avp-btn--ghost" style={{ width: "100%" }}>
                    Cancel
                  </Link>
                </div>
              </div>
            </div>
          </form>
        </div>
      </div>
    </>
  );
};

export default EditVisaPack;